import { gql } from "urql";

export const OUTREACH_PROGRAMMES_QUERY = gql`
  query OutreachProgrammes($status: String, $first: Int, $after: String) {
    outreachProgrammes(status: $status, first: $first, after: $after) {
      edges {
        node {
      id
      name
      description
      toneGuidance
      buyerPersonas
      status
      pendingDraftCount
      programmeQueries {
        id
        name
        status
        searchUrl
        scheduleCron
        nextRunAt
        lastRunAt
        lastRunStats
        lastError
      }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const OUTREACH_PROGRAMME_QUERY = gql`
  query OutreachProgramme($id: ID!) {
    outreachProgramme(id: $id) {
      id
      name
      description
      toneGuidance
      buyerPersonas
      status
      pendingDraftCount
      recentRejectionReasons
      programmeQueries {
        id
        name
        searchUrl
        scheduleCron
        status
        nextRunAt
        lastRunAt
        lastRunStats
        lastError
      }
    }
  }
`;

export const PENDING_OUTREACH_DRAFTS_QUERY = gql`
  query PendingOutreachDrafts($outreachProgrammeId: ID) {
    pendingOutreachDrafts(outreachProgrammeId: $outreachProgrammeId) {
      id
      channel
      subject
      body
      state
      createdAt
      buyer {
        id
        fullName
        title
        linkedinUrl
        prospectCompany {
          id
          name
        }
      }
      outreachProgramme {
        id
        name
      }
      jobPosting {
        id
        title
        agencyLikelihoodScore
        rawText
        evidence
        posterName
        posterCompany
      }
    }
  }
`;

export const TRIAGE_QUEUE_QUERY = gql`
  query TriageQueue($first: Int, $after: String) {
    triageQueue(first: $first, after: $after) {
      edges {
        node {
          id
          title
          companyName
          location
          posterName
          posterCompany
          posterIsAgency
          agencyLikelihoodScore
          hiringCompanyConfidence
          rawText
          evidence
          postedAt
          createdAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const ATTENTION_QUEUE_QUERY = gql`
  query AttentionQueue {
    attentionQueue {
      failedProgrammeQueries {
        id
        name
        status
        lastError
        lastRunAt
      }
      failedBuyers {
        id
        fullName
        linkedinUrl
        importStatus
        lastError
        prospectCompany {
          id
          name
        }
      }
      staleUnresolvedPostings {
        id
        title
        posterName
        posterCompany
        agencyLikelihoodScore
        createdAt
      }
    }
  }
`;

export const PROSPECT_COMPANIES_QUERY = gql`
  query ProspectCompanies($search: String, $first: Int, $after: String) {
    prospectCompanies(search: $search, first: $first, after: $after) {
      edges {
        node {
          id
          name
          domain
          linkedinUrl
          sector
          sizeBand
          hqCountry
          buyerCount
          buyers {
            id
            fullName
            title
            seniority
            linkedinUrl
            relationshipStatus
            importStatus
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const BUYER_ACTION_VIEW_QUERY = gql`
  query BuyerActionView($overdueHours: Int) {
    buyerActionView(overdueHours: $overdueHours) {
      id
      fullName
      title
      relationshipStatus
      lastSentDraftAt
      lastSentChannel
      prospectCompany {
        id
        name
      }
    }
  }
`;

export const JOB_POSTING_QUERY = gql`
  query JobPosting($id: ID!) {
    jobPosting(id: $id) {
      id
      title
      companyName
      location
      posterName
      posterTitle
      posterCompany
      posterIsAgency
      agencyLikelihoodScore
      rawText
      evidence
      sourceUrl
      applyUrl
      postedAt
      prospectCompany {
        id
        name
      }
    }
  }
`;

// === Mutations ===

export const CREATE_OUTREACH_PROGRAMME = gql`
  mutation CreateOutreachProgramme($input: CreateOutreachProgrammeInput!) {
    createOutreachProgramme(input: $input) {
      outreachProgramme { id name status }
      errors
    }
  }
`;

export const UPDATE_OUTREACH_PROGRAMME = gql`
  mutation UpdateOutreachProgramme($input: UpdateOutreachProgrammeInput!) {
    updateOutreachProgramme(input: $input) {
      outreachProgramme { id name status toneGuidance buyerPersonas }
      errors
    }
  }
`;

export const CREATE_PROGRAMME_QUERY = gql`
  mutation CreateProgrammeQuery($input: CreateProgrammeQueryInput!) {
    createProgrammeQuery(input: $input) {
      programmeQuery { id name status searchUrl nextRunAt }
      errors
    }
  }
`;

export const PAUSE_PROGRAMME_QUERY = gql`
  mutation PauseProgrammeQuery($input: PauseProgrammeQueryInput!) {
    pauseProgrammeQuery(input: $input) {
      programmeQuery { id status }
      errors
    }
  }
`;

export const RESUME_PROGRAMME_QUERY = gql`
  mutation ResumeProgrammeQuery($input: ResumeProgrammeQueryInput!) {
    resumeProgrammeQuery(input: $input) {
      programmeQuery { id status nextRunAt lastError }
      errors
    }
  }
`;

export const RUN_PROGRAMME_QUERY_NOW = gql`
  mutation RunProgrammeQueryNow($input: RunProgrammeQueryNowInput!) {
    runProgrammeQueryNow(input: $input) {
      programmeQuery { id }
      errors
    }
  }
`;

export const UPSERT_PROSPECT_COMPANY = gql`
  mutation UpsertProspectCompany($input: UpsertProspectCompanyInput!) {
    upsertProspectCompany(input: $input) {
      prospectCompany { id name }
      errors
    }
  }
`;

export const UPSERT_BUYERS_BULK = gql`
  mutation UpsertBuyersBulk($input: UpsertBuyersBulkInput!) {
    upsertBuyersBulk(input: $input) {
      buyers { id fullName linkedinUrl importStatus }
      errors
    }
  }
`;

export const APPROVE_OUTREACH_DRAFT = gql`
  mutation ApproveOutreachDraft($input: ApproveOutreachDraftInput!) {
    approveOutreachDraft(input: $input) {
      outreachDraft { id state }
      errors
    }
  }
`;

export const REJECT_OUTREACH_DRAFT = gql`
  mutation RejectOutreachDraft($input: RejectOutreachDraftInput!) {
    rejectOutreachDraft(input: $input) {
      outreachDraft { id state rejectedReason }
      errors
    }
  }
`;

export const MARK_DRAFT_SENT = gql`
  mutation MarkDraftSent($input: MarkDraftSentInput!) {
    markDraftSent(input: $input) {
      outreachDraft { id state sentAt }
      errors
    }
  }
`;

export const MARK_BUYER_ACCEPTED = gql`
  mutation MarkBuyerAccepted($input: MarkBuyerAcceptedInput!) {
    markBuyerAccepted(input: $input) {
      buyer { id relationshipStatus }
      errors
    }
  }
`;

export const MARK_BUYER_REPLIED = gql`
  mutation MarkBuyerReplied($input: MarkBuyerRepliedInput!) {
    markBuyerReplied(input: $input) {
      buyer { id relationshipStatus }
      errors
    }
  }
`;

export const OUTREACH_INBOX_QUERY = gql`
  query OutreachInbox($bucket: InboxBucketEnum!, $programmeId: ID) {
    outreachInbox(bucket: $bucket, programmeId: $programmeId) {
      id
      buyer {
        id
        fullName
        title
        linkedinUrl
        relationshipStatus
        snoozedUntil
        replyLoggedAt
        replyText
        notes
        lastSentDraftAt
        lastSentChannel
        prospectCompany {
          id
          name
        }
      }
      draft {
        id
        channel
        subject
        body
        state
        createdAt
        outreachProgramme {
          id
          name
        }
      }
      programme {
        id
        name
      }
    }
  }
`;

export const LOG_BUYER_REPLY = gql`
  mutation LogBuyerReply($input: LogBuyerReplyInput!) {
    logBuyerReply(input: $input) {
      buyer { id relationshipStatus replyLoggedAt replyText }
      errors
    }
  }
`;

export const SNOOZE_BUYER = gql`
  mutation SnoozeBuyer($input: SnoozeBuyerInput!) {
    snoozeBuyer(input: $input) {
      buyer { id snoozedUntil }
      errors
    }
  }
`;

export const UPDATE_BUYER_NOTES = gql`
  mutation UpdateBuyerNotes($input: UpdateBuyerNotesInput!) {
    updateBuyerNotes(input: $input) {
      buyer { id notes }
      errors
    }
  }
`;
